import { FormControl, FormGroup } from '@angular/forms';
import { AppModule } from '@app/app.module';
import { InternalFormValidators } from '@internal-libs/form-validators';
import { IClient } from '@internal-models/client.model';
import { AddressFormFactory } from '@shared-factories/forms/address-form.factory';
import { VALIDATION } from '@shared-libs/constants';
import { AgeCategory, UserPermissions } from '@shared-libs/enums';
import { SharedFormValidators } from '@shared-libs/form-validators';
import { IFormGroup } from '@shared-libs/interfaces';
import { UserManager } from '@shared-managers/user.manager';
import { differenceInYears, format, parse } from 'date-fns';
import { isEmpty } from 'lodash';

export class ClientFormFactory {
	private static createOptionalControls(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const userManager = AppModule.injector.get(UserManager);

		const formGroup = new FormGroup({
			id: new FormControl(client?.id),
			birthDate: new FormControl(client?.birthDate ? new Date(client.birthDate) : null, [
				SharedFormValidators.date,
			]),
			ageCategory: new FormControl(client?.ageCategory),
			...(userManager.hasAllPermissions([UserPermissions.ReadClientsSocial])
				? { social: new FormControl(client?.social, [InternalFormValidators.social]) }
				: {}),
			gender: new FormControl(client?.gender),
			digibankTargetAudience: new FormControl(client?.digibankTargetAudience),
			devices: new FormControl(client?.devices),
			note: new FormControl(client?.note),
			gdpr: new FormControl(client?.gdpr ?? false),
			ClientOriginId: new FormControl(client?.ClientOriginId),
			firstname: new FormControl(client?.firstname, [SharedFormValidators.strictString]),
			lastname: new FormControl(client?.lastname, [SharedFormValidators.strictString]),
			telephone: new FormControl(client?.telephone, [SharedFormValidators.phone]),
			landline: new FormControl(client?.landline, [SharedFormValidators.phone]),
			email: new FormControl(client?.email, [SharedFormValidators.email]),
			Address: AddressFormFactory.createAsZipRequired(client?.Address),
		}) as FormGroup;

		const setAgeCategory = (birthDate: string | Date) => {
			if (!(birthDate instanceof Date) && !VALIDATION.ALTERNATE_DATE_STRING.test(birthDate as string)) {
				return;
			}
			const date = format(
				birthDate instanceof Date ? birthDate : parse(birthDate, 'dd/MM/yyyy', new Date()),
				'yyyy-MM-dd'
			);
			formGroup.get('ageCategory').setValue(getAgeCategory(date));
			formGroup.get('ageCategory').updateValueAndValidity();
		};

		formGroup.get('birthDate').valueChanges.subscribe((birthDate: string) => {
			setAgeCategory(birthDate);
		});
		setAgeCategory(formGroup.get('birthDate').value);

		const disableGdprCheckbox = () => {
			if (isEmpty(formGroup.get('email').value) || !formGroup.get('email').valid) {
				formGroup.get('gdpr').setValue(false);
				formGroup.get('gdpr').updateValueAndValidity();
			}
		};

		formGroup.get('email').valueChanges.subscribe(() => {
			disableGdprCheckbox();
		});
		disableGdprCheckbox();

		return formGroup;
	}

	public static createForClientsPage(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const formgroup = this.createOptionalControls(client);

		formgroup.get('firstname').addValidators(SharedFormValidators.required);
		formgroup.get('lastname').addValidators(SharedFormValidators.required);
		formgroup.get('gender').addValidators(SharedFormValidators.required);
		formgroup.get('ageCategory').addValidators(SharedFormValidators.required);
		formgroup.get('devices').addValidators(SharedFormValidators.required);

		formgroup.updateValueAndValidity();
		return formgroup;
	}

	public static createForReservation(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const formgroup = this.createOptionalControls(client);
		formgroup.get('firstname').addValidators(SharedFormValidators.required);
		formgroup.get('lastname').addValidators(SharedFormValidators.required);
		formgroup.get('gender').addValidators(SharedFormValidators.required);
		formgroup.get('ageCategory').addValidators(SharedFormValidators.required);
		formgroup.get('devices').addValidators(SharedFormValidators.required);
		formgroup.updateValueAndValidity();
		return formgroup;
	}

	public static createForWaitList(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const formgroup = this.createOptionalControls(client);

		formgroup.get('firstname').addValidators(SharedFormValidators.required);
		formgroup.get('lastname').addValidators(SharedFormValidators.required);
		formgroup.get('gender').addValidators(SharedFormValidators.required);
		formgroup.get('ageCategory').addValidators(SharedFormValidators.required);
		formgroup.get('devices').addValidators(SharedFormValidators.required);

		formgroup.updateValueAndValidity();
		return formgroup;
	}

	public static createForLoan(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const formgroup = this.createOptionalControls(client);

		formgroup.get('firstname').addValidators(SharedFormValidators.required);
		formgroup.get('lastname').addValidators(SharedFormValidators.required);
		formgroup.get('gender').addValidators(SharedFormValidators.required);
		formgroup.get('ageCategory').addValidators(SharedFormValidators.required);
		formgroup.get('devices').addValidators(SharedFormValidators.required);
		formgroup.get('telephone').addValidators(SharedFormValidators.required);
		formgroup.get('digibankTargetAudience').addValidators(SharedFormValidators.required);

		formgroup.get('Address.street').addValidators(SharedFormValidators.required);
		formgroup.get('Address.number').addValidators(SharedFormValidators.required);
		formgroup.get('Address.city').addValidators(SharedFormValidators.required);

		formgroup.updateValueAndValidity();
		return formgroup;
	}

	public static createForBeegoAppointment(client?: Partial<IClient>): IFormGroup<Partial<IClient>> {
		const formgroup = this.createOptionalControls(client);

		formgroup.get('firstname').addValidators(SharedFormValidators.required);
		formgroup.get('lastname').addValidators(SharedFormValidators.required);
		formgroup.get('gender').addValidators(SharedFormValidators.required);
		formgroup.get('ageCategory').addValidators(SharedFormValidators.required);
		formgroup.get('devices').addValidators(SharedFormValidators.required);
		formgroup.get('telephone').addValidators(SharedFormValidators.required);

		formgroup.get('Address.street').addValidators(SharedFormValidators.required);
		formgroup.get('Address.number').addValidators(SharedFormValidators.required);
		formgroup.get('Address.city').addValidators(SharedFormValidators.required);

		formgroup.updateValueAndValidity();
		return formgroup;
	}

	public static getValue(clientForm: IFormGroup<Partial<IClient>>): Partial<IClient> {
		return {
			...clientForm.value,
			birthDate: clientForm.value.birthDate ? format(clientForm.value.birthDate as Date, 'yyyy-MM-dd') : null,
		};
	}
}

const getAgeCategory = (birthDate: string): AgeCategory => {
	const diff = differenceInYears(new Date(), new Date(birthDate));

	if (diff < 18) return AgeCategory.MinusEighteen;
	if (diff >= 18 && diff <= 29) return AgeCategory.PlusEighteen;
	if (diff >= 30 && diff <= 54) return AgeCategory.PlusThirty;
	if (diff >= 55) return AgeCategory.PlusFiftyFive;
};
